import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
      if (!this.refs.dialog.open) {
        this.toast({text: 'Item Added to Basket'});
      }
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  /**
   * Show a toast notification
   *
   * @param {{text: string, timeout?: number}} props
   */
  toast({ text, timeout = 3000 }) {
    if (!text) return;

    const toast = document.createElement("div");
    toast.className =
      "tw:flex tw:absolute tw:left-0 tw:right-0 tw:top-2.5 tw:z-1 tw:gap-x-3 tw:items-center tw:text-center tw:bg-[#7E7E7E] tw:px-6 tw:py-3.5 tw:uppercase tw:font-button tw:text-white";
    toast.textContent = text;

    const transitionTime = 300;

    // Prepare element off-DOM to batch class additions
    toast.classList.add(
      "tw:transition-all",
      "tw:pointer-events-none",
      "tw:duration-300",
    );

    // Batch DOM operations with requestAnimationFrame
    requestAnimationFrame(() => {
      this.refs.dialog.append(toast);

      setTimeout(() => {
        requestAnimationFrame(() => {
          toast.classList.add("tw:opacity-0", "tw:-translate-y-full");

          setTimeout(() => {
            requestAnimationFrame(() => {
              toast.remove();
            });
          }, transitionTime * 2);
        });
      }, timeout);
    });
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
